import type { ReactNode } from 'react';
import { Box, Chip, Divider, LinearProgress, Link, MenuItem, Select, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { Link as RouterLink } from 'react-router-dom';
import type { OkrKeyResult, OkrOwner, OkrTask, OkrStatus, OkrTaskStatus } from '../../api/okrs';
import { AGGREGATION_LABEL } from '../../api/okrs';
import { formatMetric, formatMonthLabel, formatMonthRange } from '../../components/formatters';

type ChipColor = 'default' | 'info' | 'success' | 'warning' | 'error';

export const STATUS_META: Record<OkrStatus, { label: string; color: ChipColor; description: string }> = {
  draft: { label: 'Draft', color: 'default', description: 'Not yet submitted for approval.' },
  submitted: { label: 'Submitted', color: 'info', description: 'Waiting for your manager to review.' },
  approved: { label: 'Approved', color: 'success', description: 'Approved by your manager.' },
  changes_requested: { label: 'Changes requested', color: 'warning', description: 'Your manager asked for changes.' },
};

export function OkrStatusChip({ status }: { status: OkrStatus }) {
  const meta = STATUS_META[status];
  return <Chip size="small" color={meta.color} label={meta.label} variant={status === 'draft' ? 'outlined' : 'filled'} />;
}

// Display order + label/colour for the 7 task statuses.
export const TASK_STATUS_ORDER: OkrTaskStatus[] = [
  'not_started',
  'in_progress',
  'on_track',
  'at_risk',
  'delayed',
  'done',
  'failed',
];

export const TASK_STATUS_META: Record<OkrTaskStatus, { label: string; color: ChipColor }> = {
  not_started: { label: 'Not started', color: 'default' },
  in_progress: { label: 'In progress', color: 'info' },
  on_track: { label: 'On track', color: 'success' },
  at_risk: { label: 'At risk', color: 'warning' },
  delayed: { label: 'Delayed', color: 'warning' },
  done: { label: 'Done', color: 'success' },
  failed: { label: 'Failed', color: 'error' },
};

// Status is always shown as a colored indicator + a neutral label, never color
// alone: done/failed get icons (the two terminal states), everything else a dot.
function StatusIndicator({ status }: { status: OkrTaskStatus }) {
  if (status === 'done') return <CheckCircleRoundedIcon sx={{ fontSize: 15, color: 'success.main' }} />;
  if (status === 'failed') return <CancelRoundedIcon sx={{ fontSize: 15, color: 'error.main' }} />;
  const color = TASK_STATUS_META[status].color;
  return (
    <Box
      component="span"
      sx={(t) => ({
        width: 8,
        height: 8,
        borderRadius: '50%',
        flexShrink: 0,
        ...(color === 'default'
          ? { boxShadow: `inset 0 0 0 1.5px ${t.palette.text.disabled}` }
          : { bgcolor: t.palette[color as 'info' | 'success' | 'warning' | 'error'].main }),
      })}
    />
  );
}

const statusPillSx = {
  borderRadius: 999,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  fontSize: 13,
  fontWeight: 500,
  lineHeight: 1.4,
  color: 'text.primary',
  whiteSpace: 'nowrap',
} as const;

export function TaskStatusChip({ status }: { status: OkrTaskStatus }) {
  return (
    <Stack
      component="span"
      direction="row"
      alignItems="center"
      gap={0.75}
      sx={{ ...statusPillSx, display: 'inline-flex', justifyContent: 'center', minWidth: 100, px: 1.25, py: 0.4 }}
    >
      <StatusIndicator status={status} />
      {TASK_STATUS_META[status].label}
    </Stack>
  );
}

// Editable counterpart of TaskStatusChip: the same pill, plus a caret.
export function TaskStatusSelect({
  value,
  disabled,
  onChange,
}: {
  value: OkrTaskStatus;
  disabled?: boolean;
  onChange: (status: OkrTaskStatus) => void;
}) {
  return (
    <Select
      variant="standard"
      disableUnderline
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as OkrTaskStatus)}
      inputProps={{ 'aria-label': 'Task status' }}
      renderValue={(v) => (
        <Stack direction="row" alignItems="center" gap={0.75}>
          <StatusIndicator status={v} />
          {TASK_STATUS_META[v].label}
        </Stack>
      )}
      sx={(t) => ({
        ...statusPillSx,
        minWidth: 124,
        transition: 'border-color 150ms, background-color 150ms, box-shadow 150ms',
        '&:hover:not(.Mui-disabled)': { bgcolor: 'action.hover' },
        '&.Mui-focused': {
          borderColor: 'primary.main',
          boxShadow: `0 0 0 3px ${alpha(t.palette.primary.main, 0.15)}`,
        },
        '&.Mui-disabled': { opacity: 0.55 },
        '& .MuiSelect-select': {
          display: 'flex',
          alignItems: 'center',
          py: 0.4,
          pl: 1.25,
          pr: '26px !important',
          minHeight: 0,
          '&:focus': { bgcolor: 'transparent' },
        },
        '& .MuiSelect-icon': { right: 5, fontSize: 18, color: 'text.secondary' },
      })}
    >
      {TASK_STATUS_ORDER.map((s) => (
        <MenuItem key={s} value={s} dense>
          <Stack direction="row" alignItems="center" gap={1}>
            <StatusIndicator status={s} />
            {TASK_STATUS_META[s].label}
          </Stack>
        </MenuItem>
      ))}
    </Select>
  );
}

// "due Sep 26" when the quarter's months are known, "due Month 3" otherwise.
export function taskDueLabel(kr: Pick<OkrKeyResult, 'monthly'>, task: Pick<OkrTask, 'monthDue'>): string {
  const month = kr.monthly[task.monthDue - 1]?.month;
  return month ? `due ${formatMonthLabel(month)}` : `due Month ${task.monthDue}`;
}

export function TaskListHeader({ count }: { count: number }) {
  return (
    <Typography
      variant="caption"
      sx={{ textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600, fontSize: 11, color: 'text.secondary' }}
    >
      Tasks · {count}
    </Typography>
  );
}

// "@Name" chip that links to the person's OKRs in the everyone view. Clicking it
// must not toggle an enclosing collapsible card, hence stopPropagation.
export function LinkedUserChip({ user }: { user: OkrOwner }) {
  return (
    <Chip
      component={RouterLink}
      to={`/okrs/all?user=${user.id}`}
      onClick={(e) => e.stopPropagation()}
      clickable
      size="small"
      variant="outlined"
      label={`@${user.name}`}
      sx={{ maxWidth: 200, fontWeight: 500 }}
    />
  );
}

// Task links are free-form — any URL is accepted on input. Only schemes that
// execute script are dropped here, since this href is rendered for other users.
function safeHref(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  const trimmed = url.trim();
  if (trimmed === '') return undefined;
  return /^\s*(javascript|data|vbscript):/i.test(trimmed) ? undefined : trimmed;
}

// One task row: name + description on the left, due month and the status
// control (chip or select) pinned right. Stacks on narrow screens.
export function TaskRow({
  task,
  due,
  control,
  linkedUser,
}: {
  task: Pick<OkrTask, 'name' | 'description' | 'notionUrl'>;
  due: string;
  control: ReactNode;
  linkedUser?: OkrOwner | null;
}) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      gap={{ xs: 0.75, sm: 2 }}
      sx={{ py: 1.25 }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {task.name}
            </Typography>
          {safeHref(task.notionUrl) && (
            <Link
              href={safeHref(task.notionUrl)}
              target="_blank"
              rel="noopener noreferrer"
              title="Open Notion page"
              sx={{ display: 'inline-flex', color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
            >
              <OpenInNewRoundedIcon sx={{ fontSize: 14 }} />
            </Link>
          )}
        </Stack>
          {linkedUser && <LinkedUserChip user={linkedUser} />}
        </Stack>
        {task.description && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {task.description}
          </Typography>
        )}
      </Box>
      <Stack direction="row" alignItems="center" gap={1.5} sx={{ flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {due}
        </Typography>
        {control}
      </Stack>
    </Stack>
  );
}

// Read-only row for a task another employee linked to this person. Mirrors
// TaskRow, but the "@" chip reads "from @author" and the status is a plain chip
// — never editable. Status stays owned by the task's author.
export function LinkedTaskRow({
  name,
  description,
  notionUrl,
  from,
  krLabel,
  due,
  status,
}: {
  name: string;
  description?: string | null;
  notionUrl?: string | null;
  from: OkrOwner | null;
  krLabel?: string;
  due: string;
  status: OkrTaskStatus;
}) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      gap={{ xs: 0.75, sm: 2 }}
      sx={{ py: 1.25 }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {name}
          </Typography>
          {safeHref(notionUrl) && (
            <Link
              href={safeHref(notionUrl)}
              target="_blank"
              rel="noopener noreferrer"
              title="Open Notion page"
              sx={{ display: 'inline-flex', color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
            >
              <OpenInNewRoundedIcon sx={{ fontSize: 14 }} />
            </Link>
          )}
          {from && (
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Typography variant="caption" color="text.secondary">
                from
              </Typography>
              <LinkedUserChip user={from} />
            </Stack>
          )}
        </Stack>
        {description && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {description}
          </Typography>
        )}
        {krLabel && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {krLabel}
          </Typography>
        )}
      </Box>
      <Stack direction="row" alignItems="center" gap={1.5} sx={{ flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {due}
        </Typography>
        <TaskStatusChip status={status} />
      </Stack>
    </Stack>
  );
}

// Read-only task list shown in team/people OKR views.
export function TaskList({ kr }: { kr: OkrKeyResult }) {
  if (kr.tasks.length === 0) return null;
  return (
    <Box sx={{ mt: 2 }}>
      <TaskListHeader count={kr.tasks.length} />
      <Stack divider={<Divider />} sx={{ mt: 0.25 }}>
        {kr.tasks.map((task) => (
          <TaskRow
            key={task.id}
            task={task}
            due={taskDueLabel(kr, task)}
            control={<TaskStatusChip status={task.status} />}
            linkedUser={task.linkedUser}
          />
        ))}
      </Stack>
    </Box>
  );
}

function clampPct(progressPct: number | null): number {
  if (progressPct == null || !Number.isFinite(progressPct)) return 0;
  return Math.max(0, Math.min(100, progressPct * 100));
}

export function KeyResultProgress({ kr }: { kr: OkrKeyResult }) {
  const hasData = kr.currentValue != null && kr.progressPct != null;
  const pctText = kr.progressPct != null ? `${Math.round(kr.progressPct * 100)}%` : '—';
  // Which months fed the figure — a summed KR spans the quarter to date, an
  // averaged or latest-month one only the months that had settled data.
  const contributing = kr.currentMonths ?? (kr.currentMonth ? [kr.currentMonth] : []);
  const rangeText = formatMonthRange(contributing);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }} gap={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {kr.label}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {[kr.layer, kr.area, AGGREGATION_LABEL[kr.aggregation]].filter(Boolean).join(' · ')}
            {kr.direction === 'down' ? ' · lower is better' : ''}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {hasData ? formatMetric(kr.currentValue as number, kr.unit, { compact: true }) : '—'}
            {' / '}
            {formatMetric(kr.targetValue, kr.unit, { compact: true })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {hasData ? `${pctText}${rangeText ? ` · ${rangeText}` : ''}` : 'No actuals yet'}
          </Typography>
        </Box>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={clampPct(kr.progressPct)}
        color={kr.progressPct != null && kr.progressPct >= 1 ? 'success' : 'primary'}
        sx={{ height: 8, borderRadius: 4, bgcolor: 'grey.100' }}
      />
      {kr.monthly.length > 0 && (
        <>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {kr.aggregation === 'sum' ? 'By month · % of the monthly pace' : 'By month · % of target'}
        </Typography>
        <Stack direction="row" sx={{ mt: 0.5 }}>
          {kr.monthly.map((m) => (
            <Box key={m.month} sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                {formatMonthLabel(m.month)}
              </Typography>
              <Typography variant="body2">
                {m.value != null ? formatMetric(m.value, kr.unit, { compact: true }) : '—'}
              </Typography>
              {m.progressPct != null && (
                <Typography variant="caption" color={m.progressPct >= 1 ? 'success.main' : 'text.secondary'}>
                  {`${Math.round(m.progressPct * 100)}%`}
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
        </>
      )}
    </Box>
  );
}
